import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import CourseCard from '../components/CourseCard';
import { getCachedCourses, setCachedCourses } from '../utils/courseCache';
import Seo from '../components/Seo';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';

const CircuitBg = () => (
  <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden', zIndex:0, opacity:0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160"/><line x1="380" y1="160" x2="680" y2="160"/>
        <line x1="980" y1="120" x2="1400" y2="120"/>
        <line x1="0" y1="420" x2="210" y2="420"/><line x1="570" y1="420" x2="880" y2="420"/>
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9"/>
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9"/>
        <circle cx="210" cy="420" r="4" fill={V2} opacity="0.9"/>
      </g>
    </svg>
  </div>
);

const ArrowRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

/* ─── SKELETON COURSE CARD ────────────────────────────────── */
const SkeletonCourseCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-thumb" />
    <div className="skeleton-body">
      {/* Badges row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div className="skeleton-bone" style={{ width:80, height:22 }} />
        <div className="skeleton-bone" style={{ width:70, height:22 }} />
      </div>
      {/* Title */}
      <div className="skeleton-bone" style={{ width:'85%', height:18 }} />
      {/* Description lines */}
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        <div className="skeleton-bone" style={{ width:'100%', height:13 }} />
        <div className="skeleton-bone" style={{ width:'72%', height:13 }} />
      </div>
      {/* Meta row */}
      <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:'1px solid var(--border)' }}>
        <div className="skeleton-bone" style={{ width:80, height:14 }} />
        <div className="skeleton-bone" style={{ width:90, height:14 }} />
      </div>
      {/* Price + CTA */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div className="skeleton-bone" style={{ width:70, height:28 }} />
        <div className="skeleton-bone" style={{ width:100, height:36, borderRadius:10 }} />
      </div>
    </div>
  </div>
);

const CourseSkeletonGrid = () => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:28 }}>
    {[...Array(6)].map((_, i) => <SkeletonCourseCard key={i} />)}
  </div>
);

const DURATION_OPTIONS = [
  { label: 'All Durations', value: '' },
  { label: '15 Days',       value: '15days' },
  { label: '1 Month',       value: '1month' },
  { label: '2 Months',      value: '2month' },
  { label: '3 Months',      value: '3month' },
];

const Courses = () => {
  const [courses, setCourses]         = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [level, setLevel]             = useState('All');
  const [durationFilter, setDurFilter]= useState('');

  useEffect(() => {
    setError(null);

    // Check client cache first — instant if available
    const cacheKey = `courses_${durationFilter}`;
    const cached = getCachedCourses(cacheKey);
    if (cached) {
      setCourses(cached);
      setFiltered(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const params = new URLSearchParams({ type: 'course' });
    if (durationFilter) params.append('durationFilter', durationFilter);
    API.get(`/courses?${params}`, { signal: controller.signal })
      .then(res => {
        setCourses(res.data);
        setFiltered(res.data);
        setCachedCourses(cacheKey, res.data);
      })
      .catch(err => { if (err.name !== 'CanceledError') setError('Failed to load courses. Please try again.'); })
      .finally(() => setLoading(false));
    return () => controller.abort(); // cancel if filter changes before request completes
  }, [durationFilter]);

  useEffect(() => {
    let result = courses;
    if (search) result = result.filter(c =>
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.category.toLowerCase().includes(search.toLowerCase())
    );
    if (level !== 'All') result = result.filter(c => c.level === level);
    setFiltered(result);
  }, [search, level, courses]);

  const btnBase = { padding:'10px 20px', borderRadius:8, fontFamily:"'Satoshi','DM Sans',sans-serif", fontWeight:700, fontSize:13.5, cursor:'pointer', border:'1.5px solid', transition:'all 0.2s' };

  return (
    <div style={{ background:'var(--bg)', color:'var(--text)' }}>
      <Seo
        title="Courses"
        description="Browse ThiranX's industry-focused tech courses in Web Development, Data Science, AI/ML, DevOps, Cybersecurity and more — with verified certificates on completion."
        path="/courses"
      />

      {/* ══ HERO ═══════════════════════════════════════════════ */}
      <section style={{ minHeight:'58vh', display:'flex', alignItems:'center', position:'relative', overflow:'hidden', padding:'120px 0 80px', background:'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position:'absolute', bottom:0, right:0, width:420, height:420, borderRadius:'50%', background:`radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents:'none' }} />
        <div className="container" style={{ position:'relative', zIndex:2, textAlign:'center' }}>
          <div className="section-label">COURSES</div>
          <h1 style={{ fontSize:'clamp(36px,6vw,70px)', fontWeight:800, lineHeight:1.1, letterSpacing:'-0.03em', marginBottom:20, fontFamily:"'Inter',sans-serif" }}>
            Learn From<br />
            <span style={{ background:`linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Industry Experts</span>
          </h1>
          <p style={{ color:'var(--text-2)', fontSize:'clamp(15px,1.8vw,18px)', maxWidth:520, lineHeight:1.8, margin:'0 auto 36px' }}>
            Comprehensive tech courses designed by industry professionals for real-world careers.
          </p>
          {/* Internship CTA Banner */}
          <Link to="/internships" style={{
            display:'inline-flex', alignItems:'center', gap:12,
            background:`linear-gradient(135deg,${V4},${V3})`,
            border:`1.5px solid ${V1}55`,
            borderRadius:14, padding:'14px 28px',
            color:'#fff', fontWeight:800, fontSize:15,
            fontFamily:"'Satoshi','DM Sans',sans-serif",
            boxShadow:`0 8px 32px ${V1}30`,
            transition:'all 0.25s cubic-bezier(.34,1.56,.64,1)',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 16px 48px ${V1}44`; e.currentTarget.style.borderColor=`${V1}99`; }}
            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=`0 8px 32px ${V1}30`; e.currentTarget.style.borderColor=`${V1}55`; }}
          >
            <BriefcaseIcon />
            <span>We Also Provide Internships</span>
            <ArrowRightIcon />
          </Link>
        </div>
      </section>

      {/* ══ FILTERS & COURSES ══════════════════════════════════ */}
      <section style={{ padding:'60px 0 120px', background:'var(--bg2)', borderTop:'1px solid var(--border)' }}>
        <div className="container">

          {/* ── Row 1: Search + Level ── */}
          <div style={{ display:'flex', gap:12, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
            <input
              type="text" placeholder="Search courses..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                flex:1, minWidth:200, background:'var(--card-solid)',
                border:`1.5px solid var(--border)`, borderRadius:10,
                color:'var(--text)', padding:'12px 16px', fontSize:14.5, outline:'none',
                fontFamily:"'Satoshi','DM Sans',sans-serif", transition:'all 0.2s',
              }}
              onFocus={e => { e.target.style.borderColor=`${V1}77`; e.target.style.boxShadow=`0 0 0 3px ${V1}18`; }}
              onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }}
            />
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {['All','Beginner','Intermediate','Advanced'].map(l => (
                <button key={l} onClick={() => setLevel(l)} style={{
                  ...btnBase,
                  background: level===l ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                  color: level===l ? '#fff' : 'var(--muted)',
                  borderColor: level===l ? V1 : 'var(--border)',
                  boxShadow: level===l ? `0 4px 16px ${V1}33` : 'none',
                }}>{l}</button>
              ))}
            </div>
          </div>

          {/* ── Row 2: Duration Filter ── */}
          <div style={{ display:'flex', gap:8, marginBottom:48, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ color:'var(--muted)', fontSize:12.5, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', marginRight:4 }}>Duration:</span>
            {DURATION_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setDurFilter(opt.value)} style={{
                ...btnBase, padding:'8px 16px',
                background: durationFilter===opt.value ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                color: durationFilter===opt.value ? '#fff' : 'var(--muted)',
                borderColor: durationFilter===opt.value ? V1 : 'var(--border)',
                boxShadow: durationFilter===opt.value ? `0 4px 16px ${V1}33` : 'none',
              }}>{opt.label}</button>
            ))}
          </div>

          {/* Course Grid */}
          {loading ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:28 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', border:`2.5px solid ${V1}33`, borderTopColor:V1, animation:'spin 0.8s linear infinite' }} />
                <span style={{ color:'var(--muted)', fontSize:14, fontWeight:600 }}>
                  Loading courses
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                </span>
              </div>
              <CourseSkeletonGrid />
            </div>
          ) : error ? (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⚠️</div>
              <div style={{ color:'var(--muted)', fontSize:15, marginBottom:20 }}>{error}</div>
              <button onClick={() => { setCachedCourses(`courses_${durationFilter}`, null); setDurFilter(f => f); }} style={{ padding:'10px 24px', borderRadius:10, border:'none', background:`linear-gradient(135deg,${V1},${V3})`, color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:"'Satoshi','DM Sans',sans-serif" }}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', color:'var(--muted)', padding:'80px 0', fontSize:16 }}>
              No courses found for these filters.
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:28, marginBottom:60 }}>
                {filtered.map(c => <CourseCard key={c._id} course={c} />)}
              </div>
              {/* Stats bar */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:20, padding:'32px 36px', borderRadius:20, background:'var(--card-solid)', border:`1px solid var(--border)` }}>
                {[
                  { val: filtered.length, label: 'Courses Shown' },
                  { val: courses.length,  label: 'Total Courses'  },
                  { val: '500+',          label: 'Students Enrolled' },
                ].map(({ val, label }) => (
                  <div key={label} style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:"'Inter',sans-serif", fontWeight:900, fontSize:32, color:V2, marginBottom:4 }}>{val}</div>
                    <div style={{ color:'var(--muted)', fontSize:13, fontWeight:600 }}>{label}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ══ INTERNSHIP PROMO BANNER ════════════════════════════ */}
      <section style={{ padding:'80px 0', background:'var(--bg)' }}>
        <div className="container">
          <div style={{
            borderRadius:24, overflow:'hidden', position:'relative',
            background:`linear-gradient(135deg,${V4} 0%,${V3} 50%,${V1} 100%)`,
            padding:'clamp(48px,6vw,80px) clamp(28px,5vw,72px)',
            display:'grid', gridTemplateColumns:'1fr auto', alignItems:'center', gap:40, flexWrap:'wrap',
          }} className="intern-promo">
            <CircuitBg />
            <div style={{ position:'relative', zIndex:1 }}>
              <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.12)', borderRadius:999, padding:'5px 16px', marginBottom:20, fontSize:12, fontWeight:800, color:'rgba(255,255,255,0.9)', letterSpacing:'0.07em' }}>
                <BriefcaseIcon />INTERNSHIP PROGRAMME
              </div>
              <h2 style={{ fontSize:'clamp(26px,3.5vw,46px)', fontWeight:900, color:'#fff', marginBottom:12, letterSpacing:'-0.025em', fontFamily:"'Inter',sans-serif" }}>
                Get Real Work Experience
              </h2>
              <p style={{ color:'rgba(255,255,255,0.72)', fontSize:16, lineHeight:1.75, maxWidth:480 }}>
                Our internship programmes give you hands-on project experience, a real certificate, and a chance to build your portfolio — in 15 days to 3 months.
              </p>
            </div>
            <div style={{ position:'relative', zIndex:1, flexShrink:0 }}>
              <Link to="/internships" style={{
                display:'inline-flex', alignItems:'center', gap:10,
                background:'#fff', color:V1,
                padding:'16px 36px', borderRadius:14, fontWeight:900, fontSize:15,
                fontFamily:"'Satoshi','DM Sans',sans-serif",
                boxShadow:'0 8px 32px rgba(0,0,0,0.22)',
                transition:'all 0.22s cubic-bezier(.34,1.56,.64,1)',
                whiteSpace:'nowrap',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 16px 48px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 8px 32px rgba(0,0,0,0.22)'; }}
              >
                Explore Internships <ArrowRightIcon />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 700px) { .intern-promo { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

export default Courses;
